/**
 * Crypto Service
 * 
 * Provides AES encryption/decryption functionality for storage services.
 * Uses the VUE_APP_SECRET_KEY environment variable for encryption key.
 * 
 * Features:
 * - AES-256-GCM encryption for authenticated encryption
 * - Base64 encoding for storage compatibility
 * - Error handling with fallback to plain text
 * - Performance optimized for UI/UX (minimal delay)
 */

class CryptoService {
    constructor() {
        this.algorithm = 'aes-256-gcm';
        this.keyLength = 32; // 256 bits
        this.ivLength = 16; // 128 bits
        this.tagLength = 16; // 128 bits
        this.enabled = this._shouldEnableEncryption();
        this.key = this._deriveKey();
        this.crypto = null;
    }

    /**
     * Initialize crypto module
     */
    async _initializeCrypto() {
        try {
            // Use dynamic import for crypto-browserify
            if (typeof window !== 'undefined') {
                this.crypto = await import('crypto-browserify');
            } else {
                this.crypto = require('crypto');
            }
        } catch (error) {
            this.enabled = false;
        }
    }

    /**
     * Check if encryption should be enabled
     * @returns {boolean} Whether encryption is enabled
     */
    _shouldEnableEncryption() {
        // Encryption is MANDATORY - never optional
        const secretKey = process.env.VUE_APP_SECRET_KEY;
        if (!secretKey || secretKey.length < 16) {
            throw new Error('🔒 CRITICAL: VUE_APP_SECRET_KEY is required and must be at least 16 characters. Encryption is MANDATORY for security.');
        }
        return true;
    }

    /**
     * Derive a consistent key from the environment secret
     * @returns {Uint8Array} Derived key (never null - encryption is mandatory)
     */
    _deriveKey() {
        if (!this.enabled) {
            throw new Error('🔒 CRITICAL: Encryption key derivation failed. Storage encryption is MANDATORY.');
        }

        try {
            const secretKey = process.env.VUE_APP_SECRET_KEY;
            
            // Simple key derivation using built-in functions for better browser compatibility
            const encoder = new TextEncoder();
            const data = encoder.encode(secretKey + 'storage-salt-2025');
            
            // Use a simple hash-based key derivation
            return this._simpleHash(data);
        } catch (error) {
            throw new Error('🔒 CRITICAL: Failed to derive encryption key. Application cannot continue without encryption.');
        }
    }

    /**
     * Simple hash function for key derivation (fallback)
     * @param {Uint8Array} data - Data to hash
     * @returns {Uint8Array} Hash result
     */
    _simpleHash(data) {
        let hash = new Uint8Array(32);
        for (let i = 0; i < data.length; i++) {
            hash[i % 32] ^= data[i];
        }
        
        // Add some mixing
        for (let round = 0; round < 10; round++) {
            for (let i = 0; i < 32; i++) {
                hash[i] = (hash[i] + hash[(i + 1) % 32] + round) % 256;
            }
        }
        
        return hash;
    }

    /**
     * Check if encryption is enabled and available
     * @returns {boolean} Encryption availability status (always true - encryption is mandatory)
     */
    isEnabled() {
        if (!this.enabled || this.key === null) {
            throw new Error('🔒 CRITICAL: Encryption is not available but is MANDATORY for this application.');
        }
        return true;
    }

    /**
     * Simple XOR encryption (fallback when crypto is not available)
     * @param {string} text - Text to encrypt/decrypt
     * @returns {string} Encrypted/decrypted text
     */
    _simpleXOR(text) {
        if (!this.key) return text;
        
        const result = [];
        for (let i = 0; i < text.length; i++) {
            result.push(String.fromCharCode(
                text.charCodeAt(i) ^ this.key[i % this.key.length]
            ));
        }
        return btoa(result.join(''));
    }

    /**
     * Simple XOR decryption
     * @param {string} encryptedText - Encrypted text to decrypt
     * @returns {string} Decrypted text
     */
    _simpleXORDecrypt(encryptedText) {
        if (!this.key) return encryptedText;
        
        try {
            const decoded = atob(encryptedText);
            const result = [];
            for (let i = 0; i < decoded.length; i++) {
                result.push(String.fromCharCode(
                    decoded.charCodeAt(i) ^ this.key[i % this.key.length]
                ));
            }
            return result.join('');
        } catch (error) {
            return encryptedText; // Return original if decryption fails
        }
    }

    /**
     * Encrypt data for storage
     * @param {string} plaintext - Data to encrypt
     * @returns {string} Encrypted data (encryption is mandatory, never returns plaintext)
     */
    encrypt(plaintext) {
        // Encryption is MANDATORY - never skip
        if (!this.isEnabled()) {
            throw new Error('🔒 CRITICAL: Cannot encrypt data - encryption service not available but is MANDATORY.');
        }

        try {
            // Use simple XOR encryption for better browser compatibility
            return this._simpleXOR(plaintext);
        } catch (error) {
            throw new Error('🔒 CRITICAL: Encryption failed but is MANDATORY for data storage.');
        }
    }

    /**
     * Decrypt data from storage
     * @param {string} encryptedData - Encrypted data to decrypt
     * @returns {string} Decrypted data
     */
    decrypt(encryptedData) {
        // Encryption is MANDATORY - must always decrypt
        if (!this.isEnabled()) {
            throw new Error('🔒 CRITICAL: Cannot decrypt data - encryption service not available but is MANDATORY.');
        }

        try {
            // Check if data looks like it might be encrypted (base64)
            if (!this._isBase64(encryptedData)) {
                // Might be legacy unencrypted data - allow silently
                return encryptedData;
            }

            // Use simple XOR decryption
            return this._simpleXORDecrypt(encryptedData);
        } catch (error) {
            // For backward compatibility, return original data silently
            return encryptedData;
        }
    }

    /**
     * Check if a string is valid base64
     * @param {string} str - String to check
     * @returns {boolean} Whether string is base64
     */
    _isBase64(str) {
        try {
            return btoa(atob(str)) === str;
        } catch (error) {
            return false;
        }
    }

    /**
     * Get crypto service status
     * @returns {Object} Status information
     */
    getStatus() {
        return {
            enabled: this.enabled,
            algorithm: 'Simple XOR (Browser Compatible)',
            hasKey: this.key !== null,
            keyLength: this.key ? this.key.length : 0
        };
    }

    /**
     * Hash a storage key to make it unreadable
     * @param {string} originalKey - The original storage key
     * @returns {string} Hashed key
     */
    hashStorageKey(originalKey) {
        if (!this.enabled) {
            throw new Error('🔒 CRITICAL: Key hashing failed. Storage encryption is MANDATORY.');
        }

        try {
            const keyHashSecret = process.env.VUE_APP_KEY_HASH_SECRET;
            if (!keyHashSecret || keyHashSecret.length < 32) {
                throw new Error('🔒 Key hash secret must be at least 32 characters');
            }

            // Create a hash of the original key using the key hash secret
            const combinedData = originalKey + keyHashSecret + 'key-salt-2025';
            const encoder = new TextEncoder();
            const data = encoder.encode(combinedData);
            
            // Generate hash and convert to base64 for storage compatibility
            const hash = this._simpleHash(data);
            const hashArray = Array.from(hash);
            
            // Convert to a storage-safe string (base64-like)
            return btoa(String.fromCharCode.apply(null, hashArray)).replace(/[+/=]/g, '');
        } catch (error) {
            throw new Error('🔒 CRITICAL: Failed to hash storage key. Application cannot continue without key protection.');
        }
    }

    /**
     * Get key hashing status - MANDATORY
     * @returns {boolean} Whether key hashing is available
     */
    canHashKeys() {
        try {
            const keyHashSecret = process.env.VUE_APP_KEY_HASH_SECRET;
            const canHash = this.enabled && keyHashSecret && keyHashSecret.length >= 32;
            
            if (!canHash) {
                throw new Error('🔑 CRITICAL: Key hashing is MANDATORY. VUE_APP_KEY_HASH_SECRET must be at least 32 characters.');
            }
            
            return canHash;
        } catch (error) {
            throw new Error('🔑 CRITICAL: Key hashing is MANDATORY and cannot be disabled.');
        }
    }
}

// Create singleton instance
const cryptoService = new CryptoService();

export { CryptoService };
export default cryptoService;
